import { useEffect, useState } from 'react'

import { ReadonlyURLSearchParams } from 'next/navigation'

import { isListedChainId } from '~/utils'

function getIsMobileDevice() {
  if (typeof navigator === 'undefined') {
    return false
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )
}

function getIsMobile() {
  if (typeof window === 'undefined') {
    return false
  }

  return getIsMobileDevice() || window.innerWidth <= 880
}

export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(getIsMobile())

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleResize = () => {
      setIsMobile(getIsMobile())
    }

    setIsMobile(getIsMobile())
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return isMobile
}

export const useIsMobileDevice = (): boolean => {
  return getIsMobileDevice()
}

/**
 * Any desktop Chromium-based browser (Chrome, Edge, Brave, Arc, Opera) — they
 * can all install extensions from the Chrome Web Store. iOS Chrome reports
 * `CriOS` (no `Chrome/`), so it's excluded twice over.
 */
export const isDesktopChromium = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false
  }

  return navigator.userAgent.includes('Chrome/') && !getIsMobileDevice()
}

export const getNavigationUri: (
  arg0: ReadonlyURLSearchParams | null,
  arg1: string,
) => string = (searchParams, uri) => {
  const chainIdParam = searchParams?.get('chainId')
  return getNavigationUriParam(
    Array.isArray(chainIdParam) ? chainIdParam[0] : chainIdParam,
    uri,
  )
}

export const getNavigationUriParam: (
  arg0: string | null | undefined,
  arg1: string,
) => string = (chainIdParam, uri) => {
  if (uri.includes('chainId=')) return uri

  let chainId
  if (chainIdParam && typeof chainIdParam === 'string') {
    try {
      chainId = parseInt(chainIdParam)
    } catch {
      // Invalid integer
      chainId = undefined
    }
  }

  return chainId && isListedChainId(chainId)
    ? updateQueryStringParameter({
        uri,
        key: 'chainId',
        value: chainId.toString(),
      })
    : uri
}

export function updateQueryStringParameter(options: {
  uri: string
  key: string
  value: string
}) {
  const { uri, key, value } = options
  const re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i')
  const separator = uri.indexOf('?') !== -1 ? '&' : '?'
  if (uri.match(re)) {
    return uri.replace(re, '$1' + key + '=' + value + '$2')
  } else {
    return uri + separator + key + '=' + value
  }
}

/**
 * Detects if the current platform is macOS
 */
export const isMacOS = (): boolean => {
  if (typeof window === 'undefined') return false
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform)
}
